import { inject, Injectable } from "@angular/core";
import { isObject } from "@app/kernel/shared/helpers";
import { MaintenanceBreakService } from "@app/shared/settings/system-settings/services/maintenance-break.service";
import { ISocketErrorInterceptor } from "../../../general/socket-io/interfaces/socket-error-interceptor.interface";

/**
 * Socket.IO error interceptor for handling **maintenance break** events.
 *
 * This interceptor listens for socket acknowledgment errors with a `reason` of `"MaintenanceBreak"`.
 * When detected, it delegates to {@link MaintenanceBreakService} to update the
 * maintenance state and potentially navigate to a maintenance page.
 *
 * This is typically registered as part of the `SOCKET_ERROR_INTERCEPTOR` multi-provider
 * so that it is automatically applied to all socket acknowledgment errors.
 *
 * @example
 * ```ts
 * // Server might respond with:
 * {
 *   error: {
 *     data: {
 *       reason: "MaintenanceBreak",
 *       maintenanceBreakStart: "2025-08-15T12:00:00Z",
 *       maintenanceBreakEnd: "2025-08-15T12:30:00Z",
 *       canSkip: false
 *     }
 *   }
 * }
 *
 * // Interceptor automatically updates maintenance break state
 * ```
 */
@Injectable()
export class WsMaintenanceBreakErrorInterceptor implements ISocketErrorInterceptor {
  private maintenanceBreakService = inject(MaintenanceBreakService);

  /**
   * Intercepts socket acknowledgment errors.
   *
   * If the error object contains `data.reason === "MaintenanceBreak"`,
   * it extracts the timing information and updates the maintenance break state.
   *
   * @param err - The raw error object from a socket acknowledgment.
   * @returns The original or transformed error (unchanged in this case).
   */
  intercept(err: unknown) {
    if (isObject(err) && "data" in err && isObject(err?.data) && "reason" in err.data && err?.data?.reason === "MaintenanceBreak") {
      this.maintenanceBreakService.handleMaintenanceBreakResponse(err.data as Record<string, string>);
    }
    return err;
  }
}
